import { useState, useEffect, type FormEvent } from 'react'
import { toast } from 'sonner'
import { Button } from '../../../shared/components/ui/Button.js'
import { Input } from '../../../shared/components/ui/Input.js'
import { Modal } from '../../../shared/components/ui/Modal.js'
import { api } from './api.js'
import { ErrorBox } from './ErrorBox.js'
import { maskCNPJ, type EmpresaFormData } from './types.js'

interface Props {
  open: boolean
  onClose: () => void
  onSaved: () => void
  contaId: string
}

export function AdicionarEmpresaModal({ open, onClose, onSaved, contaId }: Props) {
  const [form, setForm] = useState<EmpresaFormData>({ nome: '', razao_social: '', cnpj: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setForm({ nome: '', razao_social: '', cnpj: '' })
      setError('')
    }
  }, [open])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!form.nome.trim()) return
    setLoading(true)
    setError('')
    try {
      await api.post(`/api/v1/super/contas/${contaId}/empresas`, {
        nome: form.nome,
        cnpj: form.cnpj || null,
        razao_social: form.razao_social || null,
      })
      onSaved()
      onClose()
      toast.success('Empresa adicionada', { description: form.nome })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro inesperado'
      setError(msg)
      toast.error('Erro ao adicionar empresa', { description: msg })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Adicionar empresa" size="md">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-2">
        <Input
          label="Nome da farmácia"
          placeholder="Farmácia Saúde Vital"
          value={form.nome}
          onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
          autoFocus
        />
        <Input
          label="Razão Social"
          placeholder="Farmácia Saúde Vital LTDA"
          value={form.razao_social}
          onChange={(e) => setForm((f) => ({ ...f, razao_social: e.target.value }))}
        />
        <Input
          label="CNPJ"
          placeholder="00.000.000/0000-00"
          value={form.cnpj}
          onChange={(e) => setForm((f) => ({ ...f, cnpj: maskCNPJ(e.target.value) }))}
          inputMode="numeric"
        />
        {error && <ErrorBox message={error} />}
        <div className="flex justify-end gap-3 mt-2 pt-4 border-t border-[#f1f5f9]">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={loading} disabled={!form.nome.trim()}>
            Adicionar
          </Button>
        </div>
      </form>
    </Modal>
  )
}
